/**
 * Security settings card — Security & Permissions page (2026-08-11).
 *
 * Born from a real usability failure during the SOC 2 walkthrough: the
 * require-2FA mandate shipped as an API-only endpoint, and the 2FA
 * enrollment flow lives in the PROFILE modal (avatar → Password) where
 * even the founder couldn't find it. This card gives both a visible
 * home on the page where an admin would actually look:
 *
 *   1. "Require two-factor authentication" toggle — wired to
 *      GET/PATCH /api/permissions/security-settings. The server refuses
 *      to enable it unless the toggling admin has TOTP themselves
 *      (ENROL_SELF_FIRST); that refusal renders inline with a shortcut
 *      into the enrollment flow instead of a dead error.
 *   2. "Your two-factor authentication" status row — reads
 *      /api/auth/2fa/status and deep-links to the profile modal's
 *      Password tab via onOpenTwoFactorSetup (App owns that state).
 *
 * Non-admins see read-only state (the PATCH is permission-gated
 * server-side regardless — this is presentation, not enforcement).
 */

import React, { useCallback, useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://trailercst-backend-production.up.railway.app';

export default function SecuritySettingsCard({ authToken, isAdmin, onOpenTwoFactorSetup, showToast }) {
  const [require2fa, setRequire2fa] = useState(null); // null = loading
  const [selfEnrolled, setSelfEnrolled] = useState(null);
  const [saving, setSaving] = useState(false);
  const [inlineError, setInlineError] = useState(null);

  const authedFetch = useCallback((url, opts = {}) => fetch(url, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
      ...(opts.headers || {}),
    },
  }), [authToken]);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const [settings, status] = await Promise.all([
          authedFetch(`${API_BASE}/api/permissions/security-settings`).then((r) => (r.ok ? r.json() : null)),
          authedFetch(`${API_BASE}/api/auth/2fa/status`).then((r) => (r.ok ? r.json() : null)),
        ]);
        if (dead) return;
        if (settings) setRequire2fa(Boolean(settings.require2fa));
        if (status) setSelfEnrolled(Boolean(status.enabled));
      } catch {
        // Soft-fail: the card renders with unknown state rather than
        // taking down the page — roster management must stay usable.
        if (!dead) { setRequire2fa(false); setSelfEnrolled(null); }
      }
    })();
    return () => { dead = true; };
  }, [authedFetch]);

  const toggle = async () => {
    if (saving || require2fa === null) return;
    const next = !require2fa;
    setSaving(true);
    setInlineError(null);
    try {
      const res = await authedFetch(`${API_BASE}/api/permissions/security-settings`, {
        method: 'PATCH',
        body: JSON.stringify({ require2fa: next }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (payload.code === 'ENROL_SELF_FIRST') {
          setInlineError('enrol_self_first');
        } else {
          setInlineError(payload.error || `Failed to update (${res.status})`);
        }
        return;
      }
      setRequire2fa(Boolean(payload.require2fa));
      if (showToast) showToast(payload.require2fa
        ? 'Two-factor authentication is now required for all members.'
        : 'Two-factor requirement removed.');
    } catch (err) {
      setInlineError(err.message || 'Network error');
    } finally {
      setSaving(false);
    }
  };

  const rowStyle = {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    gap: 16, flexWrap: 'wrap', padding: 16, borderRadius: 12,
    border: '1px solid var(--border-color, rgba(120,120,150,0.25))',
  };
  const labelStyle = {
    fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.06em', color: '#6d28d9', marginBottom: 4,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16, marginBottom: 8 }}>
      {/* ── Org-wide mandate ─────────────────────────────────────── */}
      <div style={rowStyle}>
        <div style={{ minWidth: 240, flex: 1 }}>
          <div style={labelStyle}>Two-factor authentication policy</div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>
            {require2fa === null ? 'Loading…'
              : require2fa ? 'Required for all members' : 'Optional (member choice)'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
            {require2fa
              ? 'Members without two-factor are limited to the enrollment screen until they set it up. Disabling 2FA on an account is blocked while this is on.'
              : 'When required, every member must set up an authenticator app before using TrailerCast.'}
          </div>
          {inlineError === 'enrol_self_first' ? (
            <div style={{ marginTop: 8, fontSize: 13, color: '#b45309' }}>
              Set up two-factor on your own account first — then you can require it for everyone.
              {onOpenTwoFactorSetup && (
                <button type="button" onClick={onOpenTwoFactorSetup} style={{ marginLeft: 8, fontSize: 13, cursor: 'pointer', background: 'none', border: 'none', color: '#6d28d9', fontWeight: 600, textDecoration: 'underline', padding: 0 }}>
                  Set up now
                </button>
              )}
            </div>
          ) : inlineError ? (
            <div style={{ marginTop: 8, fontSize: 13, color: '#dc2626' }}>{inlineError}</div>
          ) : null}
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={toggle}
            disabled={saving || require2fa === null}
            style={{
              padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600,
              border: '1px solid rgba(124,58,237,0.4)',
              background: require2fa ? 'transparent' : 'linear-gradient(135deg, #7c3aed, #4f46e5)',
              color: require2fa ? '#6d28d9' : '#fff',
            }}
          >
            {saving ? 'Saving…' : require2fa ? 'Make optional' : 'Require for everyone'}
          </button>
        )}
      </div>

      {/* ── Your own enrollment ──────────────────────────────────── */}
      <div style={rowStyle}>
        <div style={{ minWidth: 240, flex: 1 }}>
          <div style={labelStyle}>Your two-factor authentication</div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>
            {selfEnrolled === null ? '—' : selfEnrolled ? 'Enabled ✓' : 'Not set up'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
            Managed from your profile (avatar → Password). Backup codes live there too.
          </div>
        </div>
        {onOpenTwoFactorSetup && (
          <button
            type="button"
            onClick={onOpenTwoFactorSetup}
            style={{ padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, border: '1px solid var(--border-color, rgba(120,120,150,0.35))', background: 'transparent', color: 'var(--text-primary, inherit)' }}
          >
            {selfEnrolled ? 'Manage' : 'Set up two-factor'}
          </button>
        )}
      </div>
    </div>
  );
}
